import {
  Avatar,
  Button,
  Card,
  Col,
  Collapse,
  notification,
  Progress,
  Row,
  Spin,
  Steps,
  Tooltip,
  Typography,
} from 'antd';
import {
  CheckOutlined,
  EnvironmentOutlined,
  HomeOutlined,
  RedoOutlined,
  RetweetOutlined,
  ShoppingOutlined,
  UserOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import 'styles/pages/RoutingDetail.less';
import moment from 'moment';
import { GoogleMap, Marker, Polygon, Polyline } from '@react-google-maps/api';
import React, { useEffect, useRef, useState } from 'react';
import { MAP_DEFAULT_VALUES, STATUS } from '../../../../helpers/constants';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import userHaveRolePermission from '../../../../helpers/userHaveRolePermission';
import { useSelector } from 'react-redux';
import {
  approveRouting,
  getReasonTypes,
  getRoutingViewListById,
  isReRoutingReq,
  isRoutingComplete,
  reRoutes,
  setSelectedRoutingDetailData,
} from '../../../../store/requests/routing';
import { ChangeDistributor } from './ChangeDistributor';
import { getGeoRadarZones } from '../../../../store/requests/zone';
import { PAGE_URLS } from '../../../../helpers/endpoints';
import { getCouriers } from '../../../../store/requests/courier';

const { Panel } = Collapse;
const { Text, Paragraph } = Typography;

const disableClass = {
  color: 'rgba(0, 0, 0, 0.25)',
  borderColor: '#d9d9d9',
  background: '#f5f5f5',
  textShadow: 'none',
  boxShadow: 'none',
};
const RoutingDetail = ({ route }) => {
  const history = useHistory();
  const selectedRoutingDetailData =
    useSelector((state) => state?.routing?.selectedRoutingDetailData) ?? null;
  const { isViewEnabled, isCrudEnabled } = userHaveRolePermission(route?.name);
  if (!isViewEnabled || !selectedRoutingDetailData) {
    history.push(`${PAGE_URLS.AUTH}${PAGE_URLS.CONTROL}`);
  }
  const map = useRef();
  const { t } = useTranslation();
  const [routeTask, setRouteTask] = useState();
  const [selectedZoneInfo, setSelectedZoneInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [zoneId, setZoneId] = useState(
    selectedRoutingDetailData?.zoneId ??
      selectedRoutingDetailData?.courierTask[0]?.slot?.zone?.id,
  );
  const [storeId, setStoreId] = useState(
    selectedRoutingDetailData?.storeId ??
      selectedRoutingDetailData?.courierTask[0]?.slot?.zone?.store?.id,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [routeReasonTypes, setRouteReasonTypes] = useState([]);
  const [againRouting, setAgainRouting] = useState(false);
  const [approveRoutingLoading, setApproveRoutingLoading] = useState(false);
  const [approveRoutingButtonDisable, setApproveRoutingButtonDisable] =
    useState(false);
  const [isReRoutingRequired, setIsReRoutingRequired] = useState(false);
  const [allCourierOfStore, setAllCourierOfStore] = useState([]);
  const [polyLine, setPolyLine] = useState(null);
  const [taskAndRouteID, setTaskAndRouteID] = useState({
    taskId: selectedRoutingDetailData?.taskId,
    routeId: selectedRoutingDetailData?.id,
  });
  const onMapLoaded = (loadedMap) => {
    map.current = loadedMap;
    defaultMapPosition();
  };
  const defaultMapPosition = (zoom = MAP_DEFAULT_VALUES.ZOOM) => {
    if (!map.current) {
      return;
    }
    map.current.setZoom(zoom);
    map.current.setCenter({
      lat: MAP_DEFAULT_VALUES.LAT,
      lng: MAP_DEFAULT_VALUES.LNG,
    });
  };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        if (selectedRoutingDetailData) {
          const reRoutingRequiredResponse = await isReRoutingReq(
            taskAndRouteID.routeId,
          );
          setIsReRoutingRequired(reRoutingRequiredResponse.data);

          const routeViewResponse = await getRoutingViewListById(
            taskAndRouteID?.routeId,
          );
          setRouteTask(routeViewResponse);

          const reasonType = await getReasonTypes();
          setRouteReasonTypes(reasonType);

          const zoneResponse = await getGeoRadarZones(
            `filter=id=in=(${zoneId})&size=2000`,
          );
          setSelectedZoneInfo(zoneResponse.content[0]);

          const getAllCouriers = await getCouriers(
            `filter=user.roles.zone.store.id==${storeId} and user.roles.role==COURIER and user.roles.active==true and user.active==true&size=2000`,
          );
          setAllCourierOfStore(getAllCouriers);

          if (againRouting) {
            setAgainRouting(false);
            setIsModalOpen(true);
          }
        }
      } catch (error) {
        console.error('error', error);
        history.push(`${PAGE_URLS.AUTH}${PAGE_URLS.CONTROL}`);
        setSelectedRoutingDetailData(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [taskAndRouteID, againRouting]);

  useEffect(() => {
    return () => {
      setSelectedRoutingDetailData(null);
    };
  }, []);

  const approveRoutingRequest = (routeID) => {
    setApproveRoutingLoading(true);
    approveRouting(routeID)
      .then((response) => {
        notificationSave(STATUS.SUCCESS);
        setApproveRoutingButtonDisable(true);
        setApproveRoutingLoading(false);
      })
      .finally(() => setApproveRoutingLoading(false));
  };

  const notificationSave = (type, desc) => {
    notification[type]({
      message: t('processCompleted'),
      description: desc ?? '',
    });
  };

  const reRoutingReq = async () => {
    reRoutes(taskAndRouteID.routeId).then((res) => {
      setIsModalOpen(false);
      setAgainRouting(false);
      notificationSave(STATUS.SUCCESS, t('routingDetail.reRoutingMessage'));
      isRoutingCompleteControl(res.data.taskId, res.data.routeId);
    });
  };

  const isRoutingCompleteControl = (taskId, routeId) => {
    setLoading(true);
    const intervalId = setInterval(() => {
      isRoutingComplete(taskId).then((isCompletedRes) => {
        if (isCompletedRes.data.isCompleted) {
          clearInterval(intervalId);
          setLoading(false);
          notificationSave(STATUS.SUCCESS, t('routingDetail.reRouteSuccess'));
          setTaskAndRouteID({
            taskId: isCompletedRes.data.taskId,
            routeId: routeId,
          });
        }
      });
    }, 5000);
  };

  useEffect(() => {
    const courierOrders = {};
    routeTask?.routeTaskDetails?.forEach((routeTask) => {
      const courierID = routeTask.courierId;

      if (!courierOrders[courierID]) {
        courierOrders[courierID] = [
          {
            lat: selectedRoutingDetailData?.storeLatLon
              ? selectedRoutingDetailData?.storeLatLon[0]
              : selectedRoutingDetailData?.courierTask[0]?.slot?.zone?.store
                  ?.latitude,
            lng: selectedRoutingDetailData?.storeLatLon
              ? selectedRoutingDetailData?.storeLatLon[1]
              : selectedRoutingDetailData?.courierTask[0]?.slot?.zone?.store
                  ?.longitude,
          },
        ];
      }

      routeTask.orders
        .slice()
        .sort((a, b) => a.deliverySortNumber - b.deliverySortNumber)
        .forEach((order) => {
          courierOrders[courierID].push({
            lat: order.latitude,
            lng: order.longitude,
          });
        });
    });

    const polylines = Object.keys(courierOrders).map((courierID) => {
      const courierCoordinate = courierOrders[courierID];

      return (
        <Polyline
          path={courierCoordinate}
          options={{
            strokeOpacity: 1,
            strokeWeight: 1.6,
          }}
        />
      );
    });

    setPolyLine(polylines);
  }, [routeTask?.routeTaskDetails]);

  return (
    <Spin spinning={loading} size="large" tip="Loading ...">
      <div>
        <div>
          <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div>
                <Tooltip
                  title={
                    routeTask?.routeTaskDetails.filter((a) =>
                      a.orders.some((b) => !b.actualOrderDeliveryTime),
                    ).length === 0
                      ? t('routingDetail.changeCourierMinReq')
                      : t('changeCourier')
                  }
                >
                  <Button
                    type="primary"
                    icon={
                      routeTask?.routeTaskDetails.filter((a) =>
                        a.orders.some((b) => !b.actualOrderDeliveryTime),
                      ).length === 0 ? (
                        <WarningOutlined />
                      ) : (
                        <RetweetOutlined />
                      )
                    }
                    style={{ marginRight: '15px' }}
                    disabled={
                      routeTask?.routeTaskDetails.filter((a) =>
                        a.orders.some((b) => !b.actualOrderDeliveryTime),
                      ).length === 0
                    }
                    onClick={() => setIsModalOpen(true)}
                  >
                    {t('changeCourier')}
                  </Button>
                </Tooltip>
                <Button
                  icon={<RedoOutlined />}
                  style={{ marginRight: '15px' }}
                  type="primary"
                  danger
                  disabled={!isReRoutingRequired}
                  onClick={reRoutingReq}
                >
                  {t('routingDetail.againRouting')}
                </Button>
                <Button
                  style={{
                    backgroundColor: '#00b96b',
                    borderColor: '#00b96b',
                    color: 'white',
                    ...(selectedRoutingDetailData?.approved ||
                    approveRoutingButtonDisable
                      ? disableClass
                      : {}),
                  }}
                  icon={<CheckOutlined />}
                  type="primary"
                  disabled={
                    selectedRoutingDetailData?.approved ||
                    approveRoutingButtonDisable
                  }
                  onClick={() => approveRoutingRequest(taskAndRouteID?.routeId)}
                  loading={approveRoutingLoading}
                >
                  {t('approve')}
                </Button>
              </div>
              <div>
                <Text strong mark>
                  {t('routeId')}{' '}
                  <Paragraph copyable={{ text: taskAndRouteID.taskId }}>
                    {taskAndRouteID?.taskId}
                  </Paragraph>
                </Text>
              </div>
            </div>
          </Card>
        </div>

        <div>
          <Card style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-around' }}>
              <div>
                <p>{t('ordersTotal')}</p>
                <p style={{ textAlign: 'center' }}>
                  {routeTask?.routeSummary?.totalOrderCount}
                </p>
              </div>
              <div>
                <p>{t('vehicle')}</p>
                <p style={{ textAlign: 'center' }}>
                  {routeTask?.routeSummary?.routeCount}
                </p>
              </div>
              <div>
                <p>{t('plannedDistance')}</p>
                <p style={{ textAlign: 'center' }}>
                  {(
                    routeTask?.routeSummary?.estimatedRouteLength / 1000
                  ).toFixed(2)}{' '}
                  (km)
                </p>
              </div>
              <div>
                <p>{t('traveledDistance')}</p>
                <p style={{ textAlign: 'center' }}>-</p>
              </div>
              <div>
                <p>{t('routingDetail.totalDurationInSecond')}</p>
                <p style={{ textAlign: 'center' }}>
                  {routeTask?.routeSummary?.estimatedRouteDuration} (dk)
                </p>
              </div>
              <div>
                <p>{t('routingDetail.orderDeliverySuccessRate')}</p>
                <div style={{ textAlign: 'center' }}>
                  <Progress
                    status="active"
                    strokeColor={{ '0%': '#108ee9', '100%': '#87d068' }}
                    percent={Math.floor(
                      routeTask?.routeSummary?.orderCompletionPercentage,
                    )}
                  />
                </div>
              </div>
            </div>
          </Card>
        </div>

        <Row>
          <Col span={8} style={{ maxHeight: '87vh', overflowY: 'auto' }}>
            {routeTask?.routeTaskDetails?.map((routeTask, index) => (
              <Card style={{ width: '100%' }} key={index}>
                <div>
                  <div>
                    <div style={{ display: 'flex' }}>
                      <Avatar
                        size="small"
                        icon={<UserOutlined />}
                        style={{
                          backgroundColor: `${routeTask.colorCode ?? '#87d068'}`,
                          marginRight: '5px',
                          color: 'black',
                        }}
                      />
                      <p>
                        <Text mark>{routeTask?.courierName}</Text>
                      </p>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <p>
                        {t('routingDetail.plannedStartAndEndTime')}:{' '}
                        {moment(routeTask?.routeStartDate).format('HH:mm')} /{' '}
                        {moment(routeTask?.routeEndDate).format('HH:mm')}
                      </p>
                      <div>
                        <ShoppingOutlined style={{ fontSize: '20px' }} />{' '}
                        {routeTask?.orderCount} {t('order')}
                      </div>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <p>
                        {t('plannedActualDistance')}:{' '}
                        {(routeTask?.estimatedRouteLength / 1000).toFixed(2)} km
                      </p>
                      <div>
                        <EnvironmentOutlined style={{ fontSize: '20px' }} />{' '}
                        {routeTask?.stationCount} {t('station')}
                      </div>
                    </div>
                  </div>
                  <div>
                    <Collapse bordered={false}>
                      <Panel
                        header={
                          <div style={{ width: '100%' }}>
                            {t('deliveryCompletionPercentage')}
                            <Progress
                              status="active"
                              strokeColor={{
                                '0%': '#108ee9',
                                '100%': '#87d068',
                              }}
                              percent={Math.floor(
                                routeTask?.deliveryCompletionPercentage,
                              )}
                            />
                          </div>
                        }
                        key="1"
                      >
                        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                          <Steps
                            direction="vertical"
                            current={2}
                            size="small"
                            className="custom-steps"
                            items={routeTask?.orders
                              .slice()
                              .sort(
                                (a, b) =>
                                  a.deliverySortNumber - b.deliverySortNumber,
                              )
                              .map((order, index) => ({
                                status: order.actualOrderDeliveryTime
                                  ? 'finish'
                                  : 'process',
                                title: (
                                  <div
                                    style={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                    }}
                                  >
                                    <p
                                      style={{ marginRight: '15px' }}
                                    >{`${order.customerOrderNumber} / ${order.customerName}`}</p>
                                    <p>
                                      <HomeOutlined /> {order.address}
                                    </p>
                                  </div>
                                ),
                                description: (
                                  <div
                                    style={
                                      order.actualOrderDeliveryTime && {
                                        color: 'rgba(0, 0, 0, 0.85)',
                                      }
                                    }
                                  >
                                    <p>
                                      {t('scheduledDeliveryDate')}:{' '}
                                      {order.estimatedOrderDeliveryTime
                                        ? moment(
                                            order.estimatedOrderDeliveryTime,
                                          ).format('DD-MM-YYYY / HH:mm')
                                        : '-'}
                                    </p>
                                    <p>
                                      {t('happeningDeliveryDate')}:{' '}
                                      {order.actualOrderDeliveryTime
                                        ? moment(
                                            order.actualOrderDeliveryTime,
                                          ).format('DD-MM-YYYY / HH:mm')
                                        : '-'}
                                    </p>
                                  </div>
                                ),
                              }))}
                          />
                        </div>
                      </Panel>
                    </Collapse>
                  </div>
                </div>
              </Card>
            ))}
          </Col>
          <Col span={16}>
            <Card style={{ width: '100%', height: '100%' }}>
              <GoogleMap
                onLoad={onMapLoaded}
                mapContainerStyle={{
                  width: '100%',
                  height: '81vh',
                }}
              >
                {routeTask?.routeTaskDetails.length > 0 &&
                  routeTask?.routeTaskDetails.map((routeTask, routeIndex) =>
                    routeTask?.orders.map((order, orderIndex) => {
                      return (
                        <Marker
                          key={`marker-${routeIndex}-${orderIndex}`}
                          position={{
                            lat: order.latitude,
                            lng: order.longitude,
                          }}
                          label={{
                            text:
                              +routeIndex +
                              1 +
                              '.' +
                              order.deliverySortNumber?.toString(),
                            className: 'delivery-location-marker-label',
                          }}
                          icon={{
                            path: 'M12 0c-4.198 0-8 3.403-8 7.602 0 4.198 3.469 9.21 8 16.398 4.531-7.188 8-12.2 8-16.398 0-4.199-3.801-7.602-8-7.602zm0 14c-3.314 0-6-2.686-6-6s2.686-6 6-6 6 2.686 6 6-2.686 6-6 6z',
                            fillColor: routeTask?.colorCode ?? 'grey',
                            fillOpacity: 1,
                            strokeWeight: 1,
                            scale: 1.8,
                            anchor:
                              window.google &&
                              new window.google.maps.Point(12, 24),
                            labelOrigin:
                              window.google &&
                              new window.google.maps.Point(12, 8),
                          }}
                        />
                      );
                    }),
                  )}
                {polyLine}
                {
                  <Marker
                    position={{
                      lat: selectedRoutingDetailData?.storeLatLon
                        ? selectedRoutingDetailData?.storeLatLon[0]
                        : selectedRoutingDetailData?.courierTask[0]?.slot?.zone
                            ?.store?.latitude,
                      lng: selectedRoutingDetailData?.storeLatLon
                        ? selectedRoutingDetailData?.storeLatLon[1]
                        : selectedRoutingDetailData?.courierTask[0]?.slot?.zone
                            ?.store?.longitude,
                    }}
                    icon="/static/img/ceptesokpin.svg"
                    width={50}
                    height={50}
                  />
                }
                {selectedZoneInfo?.zonePolygonJson && (
                  <Polygon
                    paths={selectedZoneInfo?.zonePolygonJson}
                    options={{
                      strokeOpacity: 1,
                      fillColor: 'lightgreen',
                      strokeColor: 'green',
                      editable: false,
                      fillOpacity: 0.4,
                    }}
                  />
                )}
              </GoogleMap>
            </Card>
          </Col>
        </Row>

        <div>
          {isModalOpen && (
            <ChangeDistributor
              setAgainRouting={setAgainRouting}
              isReRoutingRequired={isReRoutingRequired}
              setIsModalOpen={() => setIsModalOpen(false)}
              routeId={taskAndRouteID.routeId}
              routeReasonTypes={routeReasonTypes}
              routeTask={routeTask}
              reRoutingReq={reRoutingReq}
              allCourierOfStore={allCourierOfStore}
            />
          )}
        </div>
      </div>
    </Spin>
  );
};

export default RoutingDetail;
